export type IndustrialCaseImage = {
  pc: string;
  mobile: string;
};

export type IndustrialCaseMetric = {
  value: string;
  label: string;
};

export type IndustrialCaseAccuracyPoint = {
  value: number;
  displayValue: string;
  label: string;
};

export type IndustrialCaseFigure =
  | {
      kind: 'accuracy';
      points: IndustrialCaseAccuracyPoint[];
    }
  | {
      kind: 'flow';
    };

export type IndustrialCaseNarrativeSection = {
  heading: string;
  paragraphs: string[];
};

export type IndustrialCaseDetail = {
  overview: string;
  challenges: string[];
  outcomes: IndustrialCaseMetric[];
  narrative: IndustrialCaseNarrativeSection[];
};

export type IndustrialCaseSource = {
  id: string;
  caseNumber: string;
  customerName: string;
  title: string;
  meta: string;
  targetSound: string;
  summary: string;
  metric: IndustrialCaseMetric;
  imgUrl: IndustrialCaseImage;
  detail: IndustrialCaseDetail;
  figure: IndustrialCaseFigure;
};

export type IndustrialSoundChipSource = {
  label: string;
  highlighted?: boolean;
};

const CASE_IMAGES = {
  motor: {
    pc: '/images/apps/solution/industrial/sec_03_img_01.png',
    mobile: '/images/apps/solution/industrial/sec_03_img_01_m.png',
  },
  connector: {
    pc: '/images/apps/solution/industrial/sec_03_img_02.png',
    mobile: '/images/apps/solution/industrial/sec_03_img_02_m.png',
  },
  equipment: {
    pc: '/images/apps/solution/industrial/sec_03_img_03.png',
    mobile: '/images/apps/solution/industrial/sec_03_img_03_m.png',
  },
} as const;

const INDUSTRIAL_CASE_NARRATIVE: IndustrialCaseNarrativeSection[] = [
  {
    heading: '검사 기준을 데이터로 옮기기',
    paragraphs: [
      '현장 검사는 오랫동안 숙련자의 경험에 의존해 왔어요. 같은 소리를 듣더라도 작업자의 숙련도와 주변 환경에 따라 판단이 달라질 수 있어, 먼저 현장에서 사용하던 언어와 판정 기준을 하나씩 정리했어요',
      '프로젝트 초반에는 정상과 이상 샘플을 함께 들으며 어떤 구간에서 차이가 발생하는지 확인했어요. 단순히 음량만 비교하지 않고 주파수 변화, 반복 패턴, 제품 동작 단계처럼 판정에 영향을 주는 조건도 함께 기록했어요',
      '이렇게 정리한 기준은 모델 학습을 위한 라벨이면서 동시에 현장 담당자가 결과를 해석하는 공통 기준이 되었어요. 사람의 경험을 없애는 것이 아니라 반복 가능한 데이터 기준으로 확장하는 과정이었어요',
    ],
  },
  {
    heading: '생산 라인에 맞춘 데이터 수집',
    paragraphs: [
      '실제 생산 라인에서는 설비 자체의 구동음과 주변 공정의 충격음이 계속 섞여 들어와요. 테스트 공간에서 잘 구분되던 신호도 현장에서는 다른 소리에 가려질 수 있어, 라인이 가동되는 조건 그대로 데이터를 수집했어요',
      '동일한 제품도 속도와 부하, 작업 순서에 따라 소리가 달라질 수 있어요. 특정 시간대의 데이터만으로 판단하지 않고 여러 작업 조건과 교대 시간에 걸쳐 샘플을 확보해 변화 범위를 넓게 담았어요',
      '수집 과정은 기존 생산 흐름을 방해하지 않도록 구성했어요. 작업자가 별도의 복잡한 조작을 하지 않아도 검사 구간의 소리와 공정 정보가 함께 남도록 해 이후 분석과 재검증에 활용할 수 있게 했어요',
    ],
  },
  {
    heading: '수집한 소리를 다시 검토하기',
    paragraphs: [
      '현장에서 모은 녹음에는 검사하려는 소리만 담기지 않았어요. 설비가 움직이기 시작하는 순간의 충격음, 작업자가 제품을 다루며 내는 소리, 인접 공정에서 전달되는 진동처럼 판정과 직접 관련이 없는 신호도 함께 들어왔어요. 이 데이터를 그대로 학습에 사용하면 모델이 제품의 상태보다 주변 상황을 먼저 익힐 수 있어, 원음과 공정 기록을 나란히 놓고 사용할 구간을 다시 골랐어요',
      '라벨이 애매한 샘플은 한 번의 판단으로 확정하지 않았어요. 녹음 시점과 제품 동작 단계, 당시 설비 상태를 확인하고 현장 담당자와 같은 구간을 다시 들었어요. 정상과 이상의 경계에 놓인 소리는 별도로 모아 두고, 비슷한 사례가 추가될 때마다 기존 판단과 어긋나지 않는지 비교했어요',
      '학습에서 제외한 데이터도 버리지 않고 제외 이유와 함께 남겼어요. 주변 소음이 지나치게 크거나 검사 구간이 완전하지 않은 경우처럼 현장에서 반복될 수 있는 상황은 운영 단계에서 다시 만날 가능성이 높기 때문이에요. 모델이 답을 내기 어려운 조건을 미리 정리해 두면 자동 판정 범위와 사람의 재확인이 필요한 범위를 명확히 나눌 수 있어요',
    ],
  },
  {
    heading: '모델 검증과 현장 적용',
    paragraphs: [
      '학습 결과는 하나의 정확도 숫자만으로 판단하지 않았어요. 정상 제품을 이상으로 판단하는 오탐과 실제 이상을 놓치는 미탐을 나누어 살펴보고, 생산 운영에서 어떤 오류가 더 큰 영향을 만드는지 함께 검토했어요',
      '오탐과 미탐 사례는 원음 구간까지 다시 확인했어요. 모델의 점수와 현장 담당자의 판단이 다른 경우에는 해당 샘플의 작업 조건과 설비 상태를 추적하며 판정 기준을 보완했어요',
      '운영 전에는 현장 담당자와 함께 결과 화면과 알림 기준을 점검했어요. 모델이 확신하기 어려운 구간은 무리하게 자동 판정하지 않고 사람이 다시 확인할 수 있도록 남겨 안정적인 도입을 우선했어요',
    ],
  },
  {
    heading: '오탐과 미탐의 우선순위 정하기',
    paragraphs: [
      '정상 제품을 이상으로 판단하면 재검사와 라인 대응이 늘어나고, 실제 이상을 정상으로 판단하면 품질 문제를 뒤늦게 발견할 수 있어요. 두 오류는 같은 정확도 안에 묶이지만 현장에서 만드는 비용과 위험은 달라요. 품질 담당자와 생산 담당자가 각 오류의 영향을 함께 살펴보고 어떤 상황을 더 엄격하게 다룰지 판정 원칙부터 맞췄어요',
      '경계에 가까운 샘플은 억지로 NG나 OK 가운데 하나에 넣지 않았어요. 모델 점수와 원음, 작업 조건을 함께 확인할 수 있도록 남기고 현장 담당자가 다시 판단한 결과를 다음 검토에 반영했어요. 자동화가 모든 판단을 대신한다고 가정하기보다 확실한 구간부터 맡기고 불확실한 구간은 사람이 책임지는 구조로 운영 범위를 정했어요',
      '오류 사례가 쌓이면 단순히 전체 정확도를 다시 계산하는 데서 끝내지 않았어요. 특정 품종이나 설비 조건, 작업 순서에서 비슷한 오판이 반복되는지 묶어서 살펴봤어요. 원인이 한 조건에 집중되어 있다면 전체 기준을 흔들기보다 해당 조건의 데이터와 판정 규칙을 보완하는 쪽이 양산 안정성을 지키는 데 더 적합했어요',
    ],
  },
  {
    heading: '양산 조건에서 판정 안정화하기',
    paragraphs: [
      '모델 검증을 마친 뒤에도 기존 검사를 바로 대체하지 않았어요. 일정 기간에는 기존 청각검사와 Listen AI 판정을 함께 확인하며 두 결과가 달라지는 구간을 모았어요. 테스트 데이터에서는 드러나지 않았던 공정 소음이나 제품 편차가 실제 양산 흐름에서 나타나는지 살피는 과정이었어요',
      '교대 시간이 바뀌거나 주변 설비의 가동 상태가 달라지면 같은 제품에서도 녹음 조건이 달라질 수 있어요. 결과를 하루 단위의 평균으로만 보지 않고 시간대와 작업 조건을 나누어 확인했어요. 특정 조건에서만 점수가 흔들릴 때는 모델 전체의 문제로 단정하지 않고 마이크 위치와 설비 상태, 검사 구간이 처음과 같은지부터 점검했어요',
      '현장 적용 범위는 판정 결과와 작업자의 사용 경험을 함께 보며 넓혔어요. 결과를 확인하는 데 시간이 오래 걸리거나 재검사 사유를 파악하기 어렵다면 정확도가 높더라도 운영하기 힘들어요. 담당자가 판정 근거와 원음을 빠르게 확인하고 필요한 조치를 이어갈 수 있는지까지 살펴본 뒤 자동 판정이 맡을 구간을 정했어요',
    ],
  },
  {
    heading: '검사 운영의 변화',
    paragraphs: [
      '자동 판정이 도입된 뒤 검사자는 모든 제품의 소리를 반복해서 듣는 대신 확인이 필요한 결과에 집중할 수 있게 되었어요. 반복 업무는 줄고, 이상 원인을 분석하거나 공정을 개선하는 일에 더 많은 시간을 사용할 수 있게 되었어요',
      '축적되는 판정 이력은 제품과 설비의 변화를 시간 순서로 살펴보는 자료가 되었어요. 특정 조건에서 이상 비율이 높아지는지 확인하고, 문제가 커지기 전에 생산 조건을 점검할 수 있는 기반도 마련했어요',
      '다음 단계에서는 다른 품종과 라인으로 적용 범위를 넓히고 있어요. 새 데이터가 쌓일 때마다 기준을 다시 검토하고 모델을 보완해, 현장의 변화에 맞춰 지속적으로 운영되는 검사 체계를 만드는 것이 목표예요',
    ],
  },
  {
    heading: '라인 변화에 맞춰 기준 유지하기',
    paragraphs: [
      '양산 라인의 소리는 한 번 정한 상태로 머물지 않아요. 치공구가 교체되거나 설비 설정이 바뀌고 부품 공급 조건이 달라지면 정상 제품의 소리 범위도 조금씩 움직일 수 있어요. 초기 데이터만 기준으로 삼으면 이런 변화를 이상으로 오해할 수 있어, 판정 이력과 현장 변경 기록을 함께 보며 기준이 여전히 유효한지 확인해야 해요',
      '설비 점검이나 공정 변경이 있었던 시점은 이전과 이후의 데이터를 비교하는 기준점이 되었어요. 정상 분포가 달라졌다면 단순히 임계값을 넓히기보다 어떤 변화가 소리에 영향을 주었는지 먼저 확인했어요. 품질 문제와 자연스러운 공정 변화를 구분해야 검출 성능을 유지하면서 불필요한 재검사를 줄일 수 있어요',
      '다른 품종이나 라인으로 적용 범위를 넓힐 때도 기존 모델을 그대로 복사하지 않았어요. 새 현장의 정상음과 이상음, 주변 소음, 작업 순서를 다시 확인하고 공통으로 사용할 수 있는 기준과 새로 학습해야 할 조건을 나눴어요. 이렇게 쌓인 운영 기록은 다음 적용에서 필요한 데이터와 검증 순서를 더 빠르게 결정하는 자료가 돼요',
    ],
  },
];

export const INDUSTRIAL_SOUND_CHIPS: IndustrialSoundChipSource[] = [
  { label: '모터 이음', highlighted: true },
  { label: '커넥터 체결음', highlighted: true },
  { label: '조립 커넥터 체결음', highlighted: true },
  { label: '유리 깨짐/긁힘', highlighted: true },
  { label: '반도체 구성 장비 마모음', highlighted: true },
  { label: '로봇 설비 이음', highlighted: true },
  { label: '대형엔진 구동부 이음', highlighted: true },
  { label: '압력 밸브 이음', highlighted: true },
  { label: '모터 정상음' },
  { label: '액추에이터' },
  { label: '기어음' },
  { label: '마찰음' },
  { label: '진동음' },
  { label: '베어링음' },
  { label: '하네스 락킹음' },
  { label: '캐비테이션' },
  { label: 'NVH' },
];

export const INDUSTRIAL_CASES: IndustrialCaseSource[] = [
  {
    id: 'motor-eol-inspection',
    caseNumber: '01',
    customerName: 'A사',
    title: 'A사 모터 이음 전수검사 자동화',
    meta: '모터 품질검사',
    targetSound: '모터 이음',
    summary:
      '사람이 수행하던 전수 청각검사를 Listen AI로 자동화해 양산 라인의 NG/OK 판정 기준을 정량화했어요',
    metric: {
      value: 'NG/OK 검출률 99~100%',
      label: 'NG/OK 판정 기준',
    },
    imgUrl: CASE_IMAGES.motor,
    detail: {
      overview:
        'A사는 모터 제조가 끝난 뒤 제품마다 발생하는 소리를 검사자가 직접 듣고 NG와 OK를 판정해 왔어요. 생산량이 늘고 교대 근무가 이어질수록 같은 기준을 오래 유지하기 어려웠고, 숙련자의 판단을 신규 검사자에게 전달하는 데에도 많은 시간이 필요했어요. 이 프로젝트는 현장에서 이미 사용하던 청각검사 절차를 없애는 대신 그 안에 담긴 경험과 판정 기준을 데이터로 옮겨 Listen AI가 반복 가능한 방식으로 분석하도록 만드는 데서 시작했어요',
      narrative: INDUSTRIAL_CASE_NARRATIVE,
      challenges: [
        '생산 라인에서는 하루 동안 만들어지는 제품을 빠짐없이 검사해야 했어요. 검사자는 짧은 시간 안에 비슷한 모터 소리를 계속 들으며 정상과 이상을 구분해야 했고, 반복 작업이 길어질수록 집중력을 같은 수준으로 유지하기 어려웠어요. 생산 속도를 늦추지 않으면서 전수검사를 이어가야 한다는 점이 현장의 가장 큰 부담이었어요',
        '판정 기준은 문서에 적힌 한두 문장만으로 전달하기 어려웠어요. 숙련 검사자는 소리의 크기뿐 아니라 발생 시점과 반복되는 모양, 제품이 움직이는 단계까지 함께 듣고 판단했어요. 이런 경험이 사람마다 다르게 쌓이다 보니 같은 제품을 두고도 결과가 달라질 수 있었고, 교대 시간이나 주변 소음의 변화도 판정 일관성에 영향을 주었어요',
        '신규 검사자가 현장 기준을 익히려면 정상과 이상 샘플을 여러 번 비교하고 숙련자와 판단을 맞추는 시간이 필요했어요. 교육이 끝난 뒤에도 새로운 유형의 소리가 나타나면 다시 의견을 모아야 했고, 그 과정이 개인의 기억과 설명에 의존했어요. 검사 인력이 바뀌어도 이어지는 공통 기준과 나중에 다시 확인할 수 있는 판정 기록이 필요했어요',
      ],
      outcomes: [
        { value: '94.6%', label: 'MoC 딥러닝 정확도' },
        { value: '90.1%', label: 'EPS 구동계 정확도' },
        { value: '99~100%', label: '양산 NG/OK 검출률' },
      ],
    },
    figure: {
      kind: 'accuracy',
      points: [
        { value: 94.6, displayValue: '94.6%', label: 'MoC 딥러닝 정확도' },
        { value: 90.1, displayValue: '90.1%', label: 'EPS 구동계 정확도' },
        {
          value: 99,
          displayValue: '99~100%',
          label: '양산 NG/OK 검출률',
        },
      ],
    },
  },
  {
    id: 'connector-soft-connection',
    caseNumber: '02',
    customerName: 'B사',
    title: 'B사 커넥터 체결음 검출',
    meta: '커넥터 체결 품질검사',
    targetSound: '커넥터 체결음',
    summary:
      '전기차 변속기 커넥터의 soft connection 불량을 미세 체결음으로 판별해 체결 품질 기준을 정량화했어요',
    metric: { value: '97.73%', label: 'Cycle 판정 정확도' },
    imgUrl: CASE_IMAGES.connector,
    detail: {
      overview:
        '커넥터가 느슨하게 체결되는 문제를 사람이 듣기 어려운 미세 체결음으로 판별한 사례예요',
      narrative: INDUSTRIAL_CASE_NARRATIVE,
      challenges: [
        '미체결은 운행 안정성과 직결되는 고위험 품질 이슈였어요',
        '체결음 신호가 작아 사람 귀로 구분하기 어려운 환경이었어요',
        '작업 방식과 녹음 환경 변수가 계속 달라지는 현장이었어요',
      ],
      outcomes: [
        { value: '97.73%', label: 'Cycle 판정 정확도' },
        { value: '97%+', label: '체결음 분류 정확도' },
        { value: 'SNR 1.77dB', label: '미세 신호 검출' },
      ],
    },
    figure: {
      kind: 'accuracy',
      points: [
        {
          value: 97.73,
          displayValue: '97.73%',
          label: 'Cycle 판정 정확도',
        },
        { value: 97, displayValue: '97%+', label: '체결음 분류 정확도' },
      ],
    },
  },
  {
    id: 'assembly-connector-detection',
    caseNumber: '03',
    customerName: 'C사',
    title: 'C사 커넥터 체결음 검출',
    meta: '조립 커넥터 체결음 · 고소음 라인',
    targetSound: '조립 커넥터 체결음',
    summary:
      '80dB 수준의 조립 라인에서도 일반 커넥터와 레버 커넥터 체결음을 분리해 감지해요',
    metric: { value: '97%', label: '고소음 라인 정확도' },
    imgUrl: CASE_IMAGES.connector,
    detail: {
      overview:
        '시끄러운 조립 라인에서 여러 커넥터 유형의 체결음을 안정적으로 감지한 사례예요',
      narrative: INDUSTRIAL_CASE_NARRATIVE,
      challenges: [
        '라인 가동 시 배경 소음과 체결음의 음량 차이가 작았어요',
        '일반 커넥터와 레버 커넥터를 함께 다뤄야 했어요',
        '핀 불량처럼 미세한 결함까지 소리로 구분해야 했어요',
      ],
      outcomes: [
        { value: '97%', label: '체결음 감지 정확도' },
        { value: '80dB', label: '고소음 라인 대응' },
        { value: '99%+', label: '데이터 기반 목표 정확도' },
      ],
    },
    figure: {
      kind: 'accuracy',
      points: [
        { value: 97, displayValue: '97%', label: '체결음 감지 정확도' },
        {
          value: 99,
          displayValue: '99%+',
          label: '데이터 기반 목표 정확도',
        },
      ],
    },
  },
  {
    id: 'glass-break-scratch',
    caseNumber: '04',
    customerName: 'D사',
    title: 'D사 유리 깨짐·긁힘 감지',
    meta: '유리 공정 품질검사',
    targetSound: '유리 깨짐/긁힘',
    summary:
      '유리 절단·이송 공정의 크랙과 스크래치를 짧은 소리 신호로 포착해 불량 확산을 줄여요',
    metric: { value: '99%+', label: '유리 깨짐 감지 정확도' },
    imgUrl: CASE_IMAGES.equipment,
    detail: {
      overview:
        '모터나 커넥터가 아닌 유리 공정에서도 깨짐과 긁힘을 소리로 감지할 수 있음을 검증한 사례예요',
      narrative: INDUSTRIAL_CASE_NARRATIVE,
      challenges: [
        '스크래치 신호는 매우 짧고 작아 기존 방식으로 놓치기 쉬웠어요',
        '불량이 늦게 발견되면 후속 공정까지 손실이 커질 수 있었어요',
        '성공 시 여러 생산 테이블로 수평 전개할 수 있는 과제였어요',
      ],
      outcomes: [
        { value: '99%+', label: '유리 깨짐 감지 정확도' },
        { value: '98.6%+', label: '유리 스크래치 감지 정확도' },
        { value: '공정음', label: '유리 손상음 분석' },
      ],
    },
    figure: {
      kind: 'accuracy',
      points: [
        {
          value: 99,
          displayValue: '99%+',
          label: '유리 깨짐 감지 정확도',
        },
        {
          value: 98.6,
          displayValue: '98.6%+',
          label: '유리 스크래치 감지 정확도',
        },
      ],
    },
  },
  {
    id: 'semiconductor-component-wear',
    caseNumber: '05',
    customerName: 'E사',
    title: 'E사 반도체 부품 마모 진단',
    meta: '반도체 제조 장비 진단',
    targetSound: '반도체 구성 장비 마모음',
    summary:
      '반도체 제조 장비의 구성 부품 마모음을 분석해 교체 시점 예측에 활용해요',
    metric: { value: '90dB+', label: '고소음 현장 대응' },
    imgUrl: CASE_IMAGES.equipment,
    detail: {
      overview:
        '반도체 구성 장비의 마모 상태를 소리로 진단해 교체 판단 기준을 정량화한 사례예요',
      narrative: INDUSTRIAL_CASE_NARRATIVE,
      challenges: [
        '현장은 이어플러그가 필요한 고소음 환경이었어요',
        '정기 교체 중심 운영으로 소모품 상태 기반 판단이 어려웠어요',
        '작업자의 소리 감각에 의존하던 교체 판단을 정량화해야 했어요',
      ],
      outcomes: [
        { value: '90dB+', label: '고소음 현장 대응' },
        { value: '마모 신호', label: '소리 기반 진단 가능성' },
        { value: '마모 기준', label: '교체 판단 정량화' },
      ],
    },
    figure: { kind: 'flow' },
  },
  {
    id: 'display-robot-aging',
    caseNumber: '06',
    customerName: 'F사',
    title: 'F사 로봇 설비 이음·에이징 분석',
    meta: '대형 로봇 설비 예지보전',
    targetSound: '로봇 설비 이음',
    summary:
      'F사 공장의 이송 로봇에서 발생하는 부하·이상 징후를 소리로 분석해요',
    metric: { value: '대형 설비', label: '설비 이상 징후 분석' },
    imgUrl: CASE_IMAGES.equipment,
    detail: {
      overview:
        '대형 유리 이송 로봇의 부하와 이상 징후를 소리로 감지하는 예지보전 사례예요',
      narrative: INDUSTRIAL_CASE_NARRATIVE,
      challenges: [
        '기존 부하율 데이터만으로는 고장 징후를 충분히 판단하기 어려웠어요',
        '대형 유리 이송 설비 특성상 비파괴 진단 대안이 필요했어요',
        '현장 녹음 데이터로 부하 변화와 이상 징후를 비교했어요',
      ],
      outcomes: [
        { value: '현장녹음', label: '설비음 데이터 확보' },
        { value: '대형 로봇', label: '설비 예지보전 적용' },
        { value: '유리 공정', label: '크랙 감지 연계' },
      ],
    },
    figure: { kind: 'flow' },
  },
  {
    id: 'large-engine-drive-aging',
    caseNumber: '07',
    customerName: 'G사',
    title: 'G사 대형엔진 구동부 이음·에이징',
    meta: '대형엔진 구동부 예지보전',
    targetSound: '대형엔진 구동부 이음',
    summary:
      '대형엔진 정비 과정에서 진동과 소리 기반으로 구동부 부품 이상을 판별했어요',
    metric: { value: '모터 4종', label: '정상/이상 판별 성공' },
    imgUrl: CASE_IMAGES.motor,
    detail: {
      overview:
        '대형엔진 구동부의 베어링 계열 부품 이상을 소리와 진동으로 감지한 사례예요',
      narrative: INDUSTRIAL_CASE_NARRATIVE,
      challenges: [
        '정비 과정에서 부품 이상 여부를 사람 판단에 의존하는 구간이 있었어요',
        '차종마다 구동부 상태와 소리 조건이 달라 표준화가 필요했어요',
        '상태기반정비와 메타버스 연동까지 고려해야 했어요',
      ],
      outcomes: [
        { value: '모터 4종', label: '정상/이상 판별 성공' },
        { value: '2개년', label: '현장 데이터 분석' },
        { value: 'CBM', label: '상태기반정비 연동' },
      ],
    },
    figure: { kind: 'flow' },
  },
  {
    id: 'pressure-valve-sound',
    caseNumber: '08',
    customerName: 'H사',
    title: 'H사 가스·압력 밸브 이음 분석',
    meta: '석유화학 플랜트 안전 진단',
    targetSound: '압력 밸브 이음',
    summary:
      '석유화학 플랜트의 압력조절 밸브에서 발생하는 정상범위 이탈음을 조기 탐지해요',
    metric: { value: '이탈음', label: '정상범위 탐지 가능성' },
    imgUrl: CASE_IMAGES.equipment,
    detail: {
      overview:
        '초고압 밸브의 반복 개폐 과정에서 발생하는 이상음을 분석해 설비 안전 진단으로 연결하는 사례예요',
      narrative: INDUSTRIAL_CASE_NARRATIVE,
      challenges: [
        '볼트 파단 이력이 반복되어 조기 감지 필요성이 높았어요',
        '미세 움직임과 짧은 개폐 주기로 정상/이상 구분 난이도가 높았어요',
        '플랜트 안전 진단과 배관 누출 진단에 활용 가능한 소리였어요',
      ],
      outcomes: [
        { value: '20여 샘플', label: '현장음 분석' },
        { value: '이탈음', label: '정상범위 탐지 가능성' },
        { value: '안전 진단', label: '플랜트 이상음 분석' },
      ],
    },
    figure: { kind: 'flow' },
  },
];
